/**
 * The master refly list (MB's R24 and F06, decision 503; spec docs/specs/project-reporting/refly-list.md).
 *
 * One row for every line that Line review rejected or part-accepted, with the reflight or patch it called for. Kept by
 * itself: nothing is typed into it. Its parts come from
 * - Line review: the reviewed line (`line_status`, partial or rejected), its reason, its flight;
 * - the plan: the reflight or patch Line review planned (the next version of the line, `reflights.ts`), whose ends
 *   and length are the row's; without one, a part-accepted line's patch is the gap in its accepted data, else the
 *   patch MB's rule gives over its bad sections (`partialPatch`, issue #69), never a silent full reflight;
 * - Geosoft QC: the ZFIDs at the patch's ends (placed along the flown run, `patchEnds`) and the reason word when a
 *   contract test failed;
 * - the lines files Teslyn wrote for the nav system (`lines_file`): a row whose reflight is in one is "In a plan", and
 *   the file keeps the ZFIDs and times known when the patch was accepted.
 *
 * Status per row: **Waiting** (the reflight is planned, not in a lines file yet), **In a plan** (written to a lines
 * file for the aircraft), **Flown** (a flight with the reflight has been imported, not yet accepted), **Accepted** (the
 * reflight was accepted in Line review: the row is closed). A reflight that is itself part-accepted or rejected leaves
 * its row Flown and opens a row of its own.
 *
 * The columns are those of MB's GA example (`reference/F26019_Master_Refly_List_Day294.xlsx`, decision 503): Flt#,
 * Line #, Reason, Time Start, SPM Start, ZFID Start, East Start, North Start, Time End, SPM End, ZFID End, East End,
 * North End, Length Rejected, in the same columns, with the spacer columns D, J and P; Teslyn adds the status, the
 * block, the planned direction, the refly line, the flight it was reflown in, the lines file and the review reason.
 */
import { bearingWords, fidAtAlong, plannedRow, type LnsRow } from './lns-files.js';
import { plannedDirection } from './direction-arrows.js';
import { parseLineNumber } from './lines.js';
import type { Block, Common, Flight, LineStatus, PlannedLine, Uuid } from './model.js';
import {
  partialPatch,
  partialWithoutPatchWords,
  sectionsPatch,
  qcBadSections,
  qcFidRun,
  type BadSection,
} from './patch-suggestions.js';
import { followUpPlan, plannedLineRole, pointAlong, type PlanChange, type Segment } from './reflights.js';
import type { QcLineResult } from './geosoft-qc.js';
import { flagBadSections, type ReflyFlag } from './refly-flags.js';

export type ReflyStatus = 'waiting' | 'in_plan' | 'flown' | 'accepted';

export const REFLY_STATUS_WORDS: Readonly<Record<ReflyStatus, string>> = {
  waiting: 'Waiting',
  in_plan: 'In a plan',
  flown: 'Flown',
  accepted: 'Accepted',
};

/** The reasons as F26019 writes them, plus Teslyn's for a test it has and the GA example had not. */
export type ReflyReason = 'Diurnal' | 'XT' | 'Height' | 'Speed' | 'Noise' | 'Partial' | 'Rejected' | string;

/** What a lines file is (`lines_file.purpose`). */
export type LinesFilePurpose = 'refly' | 'patch' | 'merge' | 'split';

/** One line of a lines file Teslyn wrote, as recorded (`lines_file.lines`). */
export interface LinesFileLine {
  readonly number: number;
  readonly planned_line_id: Uuid | null;
  readonly line_status_id: Uuid | null;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly zfid_start: number | null;
  readonly zfid_end: number | null;
  /** Local `HH:MM:SS` where known (a patch accepted from Diurnal QC). */
  readonly time_start: string | null;
  readonly time_end: string | null;
  readonly reason: string;
  /**
   * The bad sections the reflight or patch answers (decision 543, 3B), as Line review accepted them: each is its own
   * row of the refly list. Absent in files written before 2026.10.7, and for lines the tools made by hand.
   */
  readonly sections?: readonly ReflySection[];
}

/** One bad section of a reviewed line, as the master refly list keeps it (decision 543, 3B: one row each). */
export interface ReflySection {
  /** Metres along the original line from its first point. */
  readonly from_m: number;
  readonly to_m: number;
  /** ZFIDs at `from_m` and `to_m`, where known. */
  readonly zfid_start: number | null;
  readonly zfid_end: number | null;
  /** Local `HH:MM:SS` at `from_m` and `to_m`, where known (Diurnal QC). */
  readonly time_start: string | null;
  readonly time_end: string | null;
  /** The reason word, as F26019 writes it (Diurnal, Height, XT, ...). */
  readonly reason: ReflyReason;
}

/** `lines_file`: a lines file Teslyn wrote for the nav system (synced; SQLite 0038, Postgres 20261025000500). */
export interface LinesFileRecord extends Common {
  readonly project_id: Uuid;
  readonly block_id: Uuid | null;
  readonly file_name: string;
  /** Folder relative to the project folder, `/`-separated, e.g. `F26048_Milgun/1. Flight Plan/Reflys`. */
  readonly folder: string;
  readonly purpose: LinesFilePurpose;
  readonly lines: readonly LinesFileLine[];
  readonly line_count: number;
  readonly length_m: number;
  readonly sha256: string;
  readonly note: string;
  readonly created_by_name: string;
}

export interface ReflyEnd {
  readonly x: number;
  readonly y: number;
  readonly zfid: number | null;
  /** Local `HH:MM:SS`, when known. */
  readonly time: string | null;
  /** Seconds past local midnight of `time`. */
  readonly spm: number | null;
}

/** The reflight or patch a row's line is to be flown with: the next version of the line, in its planned direction. */
export interface ReflyLine {
  readonly number: number;
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
  readonly zfid_start: number | null;
  readonly zfid_end: number | null;
  readonly time_start: string | null;
  readonly time_end: string | null;
  /** Its length in metres: the km still to refly. */
  readonly length_m: number;
}

export interface ReflyRow {
  /**
   * The row: the reviewed line status's id for a line with one row, `<status id>@<from>-<to>` (metres along the
   * line) for one of its bad sections (decision 543, 3B). Ticks are kept by it.
   */
  readonly id: string;
  /** The reviewed line status. */
  readonly line_status_id: Uuid;
  /** 1, 2, … for a bad section of the line, in order along it; null for the line's one row. */
  readonly section: number | null;
  /** How many bad-section rows the line has (0 for one row). */
  readonly section_count: number;
  readonly flight_id: Uuid;
  readonly flt_num: number | null;
  readonly flight_label: string;
  readonly line_number: number;
  /** As the screens write it: `100070` (decision 543, 2B; F26019 wrote `10007.0`). */
  readonly line_label: string;
  readonly acceptance: 'partial' | 'rejected';
  readonly kind: 'reflight' | 'patch';
  /** The reflight or patch line (the next version), null when none is planned. */
  readonly refly_number: number | null;
  readonly refly_line_id: Uuid | null;
  readonly block_id: Uuid | null;
  readonly block_name: string;
  readonly block_digit: string;
  /** The direction to fly, degrees from grid north: the way the original was flown, else its plan (decision 1013). */
  readonly direction_deg: number;
  readonly reason: ReflyReason;
  readonly reason_text: string;
  readonly start: ReflyEnd;
  readonly end: ReflyEnd;
  /** F26019's "Length Rejected" in metres: the bad section's length, or the reflight's for the line's one row. */
  readonly length_m: number;
  /** The reflight or patch to fly (one per reviewed line, decision 155), whichever row of the line this is. */
  readonly refly: ReflyLine;
  /** Every bad section of the line (the plan records them again). */
  readonly sections: readonly ReflySection[];
  readonly status: ReflyStatus;
  /**
   * The latest lines file the reflight was written to. Writing one does not tick "Added to aircraft" (decision 544):
   * the file is its own column.
   */
  readonly lines_file: string | null;
  /** Who wrote that lines file (F26019's "Patch Made By"). */
  readonly patch_made_by: string | null;
  readonly reflown_flt_num: number | null;
  readonly reflown_label: string | null;
  /** Decision 544: the "Checked" tick as it stands (null: never ticked). Absent from lists published before 2026.10.7. */
  readonly checked?: ReflyTickState | null;
  /** Decision 544: the "Added to aircraft" tick, ticked by hand. */
  readonly on_aircraft?: ReflyTickState | null;
  /**
   * Issue #69: set, in words, when a part-accepted line's refly is the whole line because no patch could be made (no
   * gap in the accepted data and no bad section, or a patch would cover more than half of a short line). Absent from
   * every other row and from lists published before 2026.10.11.
   */
  readonly partial_without_patch?: string;
}

export interface ReflyInput {
  readonly statuses: readonly LineStatus[];
  readonly plan: readonly PlannedLine[];
  readonly flights: readonly Flight[];
  readonly blocks: readonly Block[];
  readonly qc: readonly QcLineResult[];
  readonly linesFiles: readonly LinesFileRecord[];
  /** The rows' ticks (decision 544); none when omitted. */
  readonly ticks?: readonly ReflyTick[];
  /** Diurnal QC's kept bad sections per reviewed line (decision 563); none when omitted. */
  readonly badSections?: readonly LineBadSection[];
  /** The processor's refly flags (R127): a flagged line's rows are its flags; none when omitted. */
  readonly flags?: readonly ReflyFlag[];
}

// ------------------------------------------------------------------------------------------------- kept sections (563)

/**
 * `line_bad_section` (SQLite 0042, Postgres 20261025001200; decision 563, Q499 B): Diurnal QC's bad sections of a flown
 * line, kept when Diurnal QC finds them, so the refly list gives a line reviewed by hand a row per section too (the
 * basemag analysis behind them is not kept). The newest live row of a line status counts; synced.
 */
export interface LineBadSection extends Common {
  readonly project_id: Uuid;
  readonly line_status_id: Uuid;
  readonly source: 'diurnal';
  readonly sections: readonly ReflySection[];
}

function newestBadSections(rows: readonly LineBadSection[]): Map<Uuid, LineBadSection> {
  const out = new Map<Uuid, LineBadSection>();
  for (const r of rows) {
    if (r.deleted_at !== null) continue;
    const had = out.get(r.line_status_id);
    const later =
      !had ||
      r.updated_at > had.updated_at ||
      (r.updated_at === had.updated_at && r.created_at > had.created_at);
    if (later) out.set(r.line_status_id, r);
  }
  return out;
}

/**
 * What Diurnal QC should keep (decision 563): for each line status it found bad sections on, the sections as the
 * refly list keeps them (`reflySections`), when they differ from the newest kept row of that line (nothing is written
 * while Diurnal QC finds the same). Only Diurnal QC's own sections; Geosoft QC's are read live from its results.
 */
export function lineBadSectionChanges(
  found: ReadonlyMap<Uuid, readonly BadSection[]>,
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  qc: readonly QcLineResult[],
  kept: readonly LineBadSection[],
): Array<{ readonly line_status_id: Uuid; readonly sections: ReflySection[] }> {
  const newest = newestBadSections(kept);
  const out: Array<{ line_status_id: Uuid; sections: ReflySection[] }> = [];
  for (const [id, list] of found) {
    const status = statuses.find((s) => s.id === id && s.deleted_at === null);
    if (!status) continue;
    const sections = reflySections(
      list.filter((b) => b.source === 'diurnal'),
      status,
      plan,
      qc,
    );
    if (sections.length === 0) continue;
    const had = newest.get(id);
    if (had && JSON.stringify(had.sections) === JSON.stringify(sections)) continue;
    out.push({ line_status_id: id, sections });
  }
  return out;
}

/** The newest live Geosoft QC result of a line status with a ZFID range, or undefined. */
function newestQcResult(status: LineStatus, qc: readonly QcLineResult[]): QcLineResult | undefined {
  return qc
    .filter((r) => r.deleted_at === null && r.line_status_id === status.id && r.fid_start !== null)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
}

/**
 * The bad sections QC found on a reviewed line, as `partialPatch` and `suggestPatch` take them: Diurnal QC's kept
 * sections (decision 563, the newest live row) and the failed events of the line's newest Geosoft QC result. Line
 * review's "Partial" and the refly list use the same (issue #69).
 */
export function foundBadSections(
  status: LineStatus,
  plan: readonly PlannedLine[],
  qc: readonly QcLineResult[],
  kept: readonly LineBadSection[],
): BadSection[] {
  const result = newestQcResult(status, qc);
  return [
    ...(newestBadSections(kept).get(status.id)?.sections ?? []).map((x): BadSection => ({
      source: 'diurnal',
      from_m: x.from_m,
      to_m: x.to_m,
      label: x.reason,
      times: { from: x.time_start, to: x.time_end },
    })),
    ...(result ? qcBadSections(result, status, plan) : []),
  ];
}

// ------------------------------------------------------------------------------------------------- ticks (544)

/** The refly list's two ticks (decision 544): "Checked", and "Added to aircraft" (ticked by hand). */
export type ReflyTickKind = 'checked' | 'on_aircraft';

export const REFLY_TICK_WORDS: Readonly<Record<ReflyTickKind, string>> = {
  checked: 'Checked',
  on_aircraft: 'Added to aircraft',
};

/** `refly_tick` (SQLite 0042, Postgres 20261025001200): one tick or untick of a row; never changed. */
export interface ReflyTick extends Common {
  readonly project_id: Uuid;
  /** The row's id (`ReflyRow.id`). */
  readonly row_key: string;
  readonly line_status_id: Uuid | null;
  readonly tick: ReflyTickKind;
  readonly ticked: boolean;
  /** Who (Teslyn Cloud stamps the signed-in person's name). */
  readonly by_name: string;
}

/** A tick as it stands: its newest row. */
export interface ReflyTickState {
  readonly ticked: boolean;
  readonly by_name: string;
  readonly at: string;
}

/** Every tick and untick of a row's tick, newest first (deleted rows left out). */
export function reflyTickHistory(
  ticks: readonly ReflyTick[],
  rowKey: string,
  kind: ReflyTickKind,
): ReflyTick[] {
  return ticks
    .filter((t) => t.deleted_at === null && t.row_key === rowKey && t.tick === kind)
    .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id));
}

function tickState(ticks: readonly ReflyTick[], rowKey: string, kind: ReflyTickKind): ReflyTickState | null {
  const t = reflyTickHistory(ticks, rowKey, kind)[0];
  return t ? { ticked: Boolean(t.ticked), by_name: t.by_name, at: t.created_at } : null;
}

const cm = (m: number): number => Math.round(m * 100) / 100;

/** `100070` → `10007.0`, as the GA example and the QC log write a line. */
export function reflyLineLabel(n: number): string {
  const s = String(n);
  return `${s.slice(0, -1)}.${s.slice(-1)}`;
}

/** Seconds past midnight of a local `HH:MM:SS`, or null. */
export function spmOf(time: string | null): number | null {
  const m = time ? /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(time) : null;
  return m ? Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3]) : null;
}

const QC_REASONS: ReadonlyArray<[RegExp, ReflyReason]> = [
  [/^flight_path$/, 'XT'],
  [/^(terrain_clearance|hard_deck|lower_working_height)$/, 'Height'],
  [/^(sample_interval|speed)$/, 'Speed'],
  [/^(mag_noise|daily_noise.*)$/, 'Noise'],
];

const TEXT_REASONS: ReadonlyArray<[RegExp, ReflyReason]> = [
  [/diurnal/i, 'Diurnal'],
  [/not flown in full|part(ly)?[ -]flown|partial|clipped/i, 'Partial'],
  [/cross[ -]?track|flight path|\bXT\b/i, 'XT'],
  [/height|clearance|terrain|hard deck|low cloud/i, 'Height'],
  [/speed|sample interval/i, 'Speed'],
  [/noise|spike/i, 'Noise'],
];

/** The reason word of a reviewer's or processor's words (Diurnal, Partial, XT, ...), or null. */
export function textReasonWord(text: string): ReflyReason | null {
  return TEXT_REASONS.find(([re]) => re.test(text))?.[1] ?? null;
}

/** The reason word of a Geosoft QC test (F26019's words), or null for a test it has none for. */
export function qcReasonWord(testKey: string): ReflyReason | null {
  return QC_REASONS.find(([re]) => re.test(testKey))?.[1] ?? null;
}

/**
 * The bad sections of a reviewed line as the refly list keeps them (decision 543, 3B), from the suggestion Line
 * review accepted: in order along the line, ZFIDs from the section (Geosoft QC) or placed along the line's newest
 * Geosoft QC run, times from Diurnal QC.
 */
export function reflySections(
  sections: readonly BadSection[],
  status: LineStatus,
  plan: readonly PlannedLine[],
  qc: readonly QcLineResult[],
): ReflySection[] {
  const original = originalOf(
    status.line_number,
    plan.filter((p) => p.deleted_at === null),
  );
  const result = qc
    .filter((r) => r.deleted_at === null && r.line_status_id === status.id && r.fid_start !== null)
    .sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  const run = result && original ? qcFidRun(result, status, original) : null;
  const zfid = (along: number): number | null => (run ? fidAtAlong(run, along) : null);
  const out: ReflySection[] = [];
  for (const b of [...sections].sort((x, y) => x.from_m - y.from_m || x.to_m - y.to_m)) {
    const reason: ReflyReason =
      b.source === 'diurnal'
        ? 'Diurnal'
        : b.source === 'processor'
          ? // R127: the QC test the flag was picked from, else the flag's own words, else Processor
            (qcReasonWord(b.test_key ?? '') ?? textReasonWord(b.reason_text ?? '') ?? 'Processor')
          : (qcReasonWord(b.test_key ?? '') ?? (status.acceptance === 'partial' ? 'Partial' : 'Rejected'));
    const sec: ReflySection = {
      from_m: cm(b.from_m),
      to_m: cm(b.to_m),
      zfid_start: b.zfids?.from ?? zfid(b.from_m),
      zfid_end: b.zfids?.to ?? zfid(b.to_m),
      time_start: b.times?.from ?? null,
      time_end: b.times?.to ?? null,
      reason,
    };
    const same = out.find((o) => o.from_m === sec.from_m && o.to_m === sec.to_m && o.reason === sec.reason);
    if (!same) out.push(sec);
  }
  return out;
}

/**
 * The reason word of a reviewed line, as F26019 writes it: the failed Geosoft QC contract test (flight path XT,
 * terrain clearance and hard deck Height, sample interval Speed, mag noise Noise), else the reviewer's words (Diurnal,
 * Partial for a line not flown in full, ...), else Partial or Rejected.
 */
export function reflyReason(status: LineStatus, qc: readonly QcLineResult[]): ReflyReason {
  for (const r of qc) {
    if (r.deleted_at !== null || r.line_status_id !== status.id) continue;
    for (const t of r.tests) {
      if (t.status !== 'fail') continue;
      const hit = QC_REASONS.find(([re]) => re.test(t.key));
      if (hit) return hit[1];
    }
  }
  for (const [re, word] of TEXT_REASONS) if (re.test(status.reason)) return word;
  return status.acceptance === 'partial' ? 'Partial' : 'Rejected';
}

function originalOf(n: number, plan: readonly PlannedLine[]): PlannedLine | undefined {
  const base = parseLineNumber(n).base;
  return plan.find((p) => p.deleted_at === null && p.number === base && plannedLineRole(p) === 'original');
}

/** The rows of the master refly list, in flight then line order. */
export function reflyRows(input: ReflyInput): ReflyRow[] {
  const live = input.statuses.filter((s) => s.deleted_at === null);
  const plan = input.plan.filter((p) => p.deleted_at === null);
  const flightById = new Map(input.flights.map((f) => [f.id, f]));
  const blockById = new Map(input.blocks.map((b) => [b.id, b]));
  const ticks = input.ticks ?? [];
  const kept = input.badSections ?? [];
  const keptOf = newestBadSections(kept);
  const flagsOf = flagBadSections(input.flags ?? []);
  const files = input.linesFiles
    .filter((f) => f.deleted_at === null)
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
  const label = (id: Uuid): string => {
    const f = flightById.get(id);
    return f ? `Day ${f.jday} flt ${f.flt_num}` : '';
  };
  const out: ReflyRow[] = [];
  for (const s of live) {
    if (s.acceptance !== 'partial' && s.acceptance !== 'rejected') continue;
    const parsed = parseLineNumber(s.line_number);
    if (!parsed.production) continue;
    const original = originalOf(s.line_number, plan);
    if (!original) continue;
    const next = s.line_number + 1;
    const refly = plan.find((p) => p.number === next && p.reflight_of_line_id === original.id);
    const result = newestQcResult(s, input.qc);
    const run = result ? qcFidRun(result, s, original) : null;
    // the processor's refly flags of the line (R127), oldest first
    const flagged = flagsOf.get(s.id) ?? [];
    let seg: Segment;
    let kind: 'reflight' | 'patch';
    let row: LnsRow;
    let partialWithoutPatch: string | undefined;
    if (refly) {
      kind = plannedLineRole(refly) === 'patch' ? 'patch' : 'reflight';
      seg =
        kind === 'patch'
          ? { from_m: refly.segment_from_m ?? 0, to_m: refly.segment_to_m ?? refly.length_m }
          : { from_m: 0, to_m: original.length_m };
      row = plannedRow(refly);
    } else {
      // issue #69: no patch planned (a TJR import, or a Partial saved before 2026.10.11): the gap in the accepted
      // data, else the patch MB's rule gives over the bad sections; the whole line only said in words
      // R127: a flagged line's patch comes from its flags only (not the gap, not the sections QC found)
      const d =
        s.acceptance !== 'partial'
          ? null
          : flagged.length > 0
            ? sectionsPatch(s, original, plan, flagged)
            : partialPatch(s, s.accepted_m, original, plan, foundBadSections(s, plan, input.qc, kept));
      const patch = d !== null && d.suggestion?.kind !== 'reflight';
      kind = patch ? 'patch' : 'reflight';
      seg = patch ? d.segment : { from_m: 0, to_m: original.length_m };
      if (s.acceptance === 'partial' && !patch)
        partialWithoutPatch = partialWithoutPatchWords(s.line_number, d?.suggestion ?? null);
      const [x1, y1] = pointAlong(original, seg.from_m);
      const [x2, y2] = pointAlong(original, seg.to_m);
      row = { number: next, x1: cm(x1), y1: cm(y1), x2: cm(x2), y2: cm(y2) };
    }
    const file = files.find((f) =>
      f.lines.some(
        (l) =>
          (refly && l.planned_line_id === refly.id) ||
          l.line_status_id === s.id ||
          (l.number === next && (f.block_id === null || f.block_id === original.block_id)),
      ),
    );
    const written = file?.lines.find(
      (l) => (refly && l.planned_line_id === refly.id) || l.line_status_id === s.id || l.number === next,
    );
    // the file's ZFIDs and times are the refly's only when the file holds the same line (issue #69: a file written
    // with the full line keeps the row In a plan, but its ZFIDs are not the patch's)
    const sameLine = (l: LinesFileLine): boolean =>
      Math.hypot(l.x1 - row.x1, l.y1 - row.y1) < 1 && Math.hypot(l.x2 - row.x2, l.y2 - row.y2) < 1;
    const stored = written && sameLine(written) ? written : undefined;
    const zfid = (along: number): number | null => (run ? fidAtAlong(run, along) : null);
    const end = (x: number, y: number, z: number | null, time: string | null): ReflyEnd => ({
      x,
      y,
      zfid: z,
      time,
      spm: spmOf(time),
    });
    // the reflight as flown: its version of the line in any flight (a TJR import links by number only)
    const reflown = live.filter((x) => x.line_number === next);
    const accepted = reflown.find((x) => x.acceptance === 'accepted');
    const flownOne = accepted ?? reflown[0];
    const status: ReflyStatus = accepted
      ? 'accepted'
      : reflown.length > 0
        ? 'flown'
        : file
          ? 'in_plan'
          : 'waiting';
    const flight = flightById.get(s.flight_id);
    const block = blockById.get(original.block_id);
    const reflyLine: ReflyLine = {
      number: row.number,
      x1: row.x1,
      y1: row.y1,
      x2: row.x2,
      y2: row.y2,
      zfid_start: stored?.zfid_start ?? zfid(seg.from_m),
      zfid_end: stored?.zfid_end ?? zfid(seg.to_m),
      time_start: stored?.time_start ?? null,
      time_end: stored?.time_end ?? null,
      length_m: cm(seg.to_m - seg.from_m),
    };
    // decision 543 (3B): a row per bad section the reflight answers: the processor's refly flags (R127, the zFids as
    // flagged), else as Line review accepted them (recorded in the lines file), else Diurnal QC's kept sections
    // (decision 563) with the newest Geosoft QC result's; only what lies in the reflight, cut to it
    const recorded =
      flagged.length > 0 ? reflySections(flagged, s, plan, input.qc) : (written?.sections ?? []);
    const sections = (
      recorded.length > 0
        ? recorded
        : [
            ...(keptOf.get(s.id)?.sections ?? []),
            ...(result ? reflySections(qcBadSections(result, s, plan), s, plan, input.qc) : []),
          ].sort((a, b) => a.from_m - b.from_m || a.to_m - b.to_m)
    )
      .filter((x) => x.to_m > seg.from_m + 0.005 && x.from_m < seg.to_m - 0.005)
      .map((x) => ({ ...x, from_m: Math.max(x.from_m, seg.from_m), to_m: Math.min(x.to_m, seg.to_m) }));
    const shared = {
      line_status_id: s.id,
      flight_id: s.flight_id,
      flt_num: flight?.flt_num ?? null,
      flight_label: label(s.flight_id),
      line_number: s.line_number,
      line_label: String(s.line_number),
      acceptance: s.acceptance,
      kind,
      refly_number: refly ? refly.number : null,
      refly_line_id: refly?.id ?? null,
      block_id: block?.id ?? original.block_id,
      block_name: block?.name ?? '',
      block_digit: block?.digit ?? parsed.blockDigit ?? '',
      // the way the original was flown, else its plan (decision 1013); the maps' arrows point this way too
      direction_deg: plannedDirection(original, null, live).deg,
      reason_text: s.reason,
      refly: reflyLine,
      sections,
      status,
      lines_file: file?.file_name ?? null,
      patch_made_by: file?.created_by_name || null,
      reflown_flt_num: flownOne ? (flightById.get(flownOne.flight_id)?.flt_num ?? null) : null,
      reflown_label: flownOne ? label(flownOne.flight_id) : null,
      ...(partialWithoutPatch !== undefined ? { partial_without_patch: partialWithoutPatch } : {}),
    } as const;
    const ticked = (id: string) => ({
      checked: tickState(ticks, id, 'checked'),
      on_aircraft: tickState(ticks, id, 'on_aircraft'),
    });
    if (sections.length === 0) {
      out.push({
        ...shared,
        ...ticked(s.id),
        id: s.id,
        section: null,
        section_count: 0,
        reason: stored?.reason || reflyReason(s, input.qc),
        start: end(row.x1, row.y1, reflyLine.zfid_start, reflyLine.time_start),
        end: end(row.x2, row.y2, reflyLine.zfid_end, reflyLine.time_end),
        length_m: reflyLine.length_m,
      });
      continue;
    }
    sections.forEach((x, k) => {
      const [x1, y1] = pointAlong(original, x.from_m);
      const [x2, y2] = pointAlong(original, x.to_m);
      const id = `${s.id}@${Math.round(x.from_m)}-${Math.round(x.to_m)}`;
      out.push({
        ...shared,
        ...ticked(id),
        id,
        section: k + 1,
        section_count: sections.length,
        reason: x.reason,
        start: end(cm(x1), cm(y1), x.zfid_start ?? zfid(x.from_m), x.time_start),
        end: end(cm(x2), cm(y2), x.zfid_end ?? zfid(x.to_m), x.time_end),
        length_m: cm(x.to_m - x.from_m),
      });
    });
  }
  const dateOf = (r: ReflyRow): string => flightById.get(r.flight_id)?.date ?? '';
  return out.sort(
    (a, b) =>
      dateOf(a).localeCompare(dateOf(b)) ||
      (a.flt_num ?? 0) - (b.flt_num ?? 0) ||
      a.line_number - b.line_number ||
      (a.section ?? 0) - (b.section ?? 0),
  );
}

/** Open rows: not yet accepted. */
export const isOpenRefly = (r: Pick<ReflyRow, 'status'>): boolean => r.status !== 'accepted';

export interface ReflyTotals {
  readonly open_rows: number;
  readonly closed_rows: number;
  /** Km still to refly (open rows). */
  readonly open_km: number;
  readonly by_reason: ReadonlyArray<{ readonly reason: string; readonly rows: number; readonly km: number }>;
  readonly by_block: ReadonlyArray<{
    readonly block_id: string | null;
    readonly block_name: string;
    readonly rows: number;
    readonly km: number;
  }>;
}

/**
 * One row per reviewed line: its first (the reflight is the same for every section row of a line). A row published
 * before 2026.10.7 has no `line_status_id`: its id is the line status's.
 */
function perLine(rows: readonly ReflyRow[]): ReflyRow[] {
  const seen = new Set<string>();
  const out: ReflyRow[] = [];
  for (const r of rows) {
    const key = r.line_status_id ?? r.id;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/**
 * Totals of the open rows per reason (most km first) and per block (block order), in km with two decimals. Rows are
 * counted as listed (a row per bad section, decision 543); km are the reflights' still to fly, each line once, under
 * the reason of its first row.
 */
export function reflyTotals(rows: readonly ReflyRow[]): ReflyTotals {
  const open = rows.filter(isOpenRefly);
  const kmOf = new Map(perLine(open).map((r) => [r.id, r.refly?.length_m ?? r.length_m]));
  const reasons = new Map<string, { rows: number; m: number }>();
  const blocks = new Map<
    string,
    { block_id: string | null; block_name: string; digit: string; rows: number; m: number }
  >();
  for (const r of open) {
    const m = kmOf.get(r.id) ?? 0;
    const a = reasons.get(r.reason) ?? { rows: 0, m: 0 };
    a.rows += 1;
    a.m += m;
    reasons.set(r.reason, a);
    const key = r.block_id ?? '';
    const b = blocks.get(key) ?? {
      block_id: r.block_id,
      block_name: r.block_name,
      digit: r.block_digit,
      rows: 0,
      m: 0,
    };
    b.rows += 1;
    b.m += m;
    blocks.set(key, b);
  }
  const km = (m: number): number => Math.round(m / 10) / 100;
  return {
    open_rows: open.length,
    closed_rows: rows.length - open.length,
    open_km: [...kmOf.values()].reduce((t, m) => t + m, 0) / 1000,
    by_reason: [...reasons.entries()]
      .map(([reason, v]) => ({ reason, rows: v.rows, km: km(v.m) }))
      .sort((a, b) => b.km - a.km || a.reason.localeCompare(b.reason)),
    by_block: [...blocks.values()]
      .sort((a, b) => a.digit.localeCompare(b.digit) || a.block_name.localeCompare(b.block_name))
      .map((b) => ({ block_id: b.block_id, block_name: b.block_name, rows: b.rows, km: km(b.m) })),
  };
}

/**
 * The lines a lines file records for these rows: each line's reflight or patch once, with what is known of its ends
 * and the bad sections it answers (decision 543, 3B).
 */
export function reflyLinesFileLines(rows: readonly ReflyRow[]): LinesFileLine[] {
  return perLine(rows).map((r) => ({
    number: r.refly.number,
    planned_line_id: r.refly_line_id,
    line_status_id: r.line_status_id,
    x1: r.refly.x1,
    y1: r.refly.y1,
    x2: r.refly.x2,
    y2: r.refly.y2,
    zfid_start: r.refly.zfid_start,
    zfid_end: r.refly.zfid_end,
    time_start: r.refly.time_start,
    time_end: r.refly.time_end,
    reason: r.reason,
    ...(r.sections.length > 0 ? { sections: r.sections } : {}),
  }));
}

/** The `.lns` rows of these refly rows: each line's reflight or patch once, in the line's planned direction. */
/**
 * The direction to fly each row of `reflyLnsRows(rows)`, in the same order (decision 1013: the way the original was
 * flown): the "Make a plan (.lns)" preview draws its arrow this way. The file keeps the rows' planned point order.
 */
export function reflyLnsDirections(rows: readonly ReflyRow[]): number[] {
  return perLine(rows).map((r) => r.direction_deg);
}

export function reflyLnsRows(rows: readonly ReflyRow[]): LnsRow[] {
  return perLine(rows).map((r) => ({
    number: r.refly.number,
    x1: r.refly.x1,
    y1: r.refly.y1,
    x2: r.refly.x2,
    y2: r.refly.y2,
  }));
}

// ------------------------------------------------------------------------------------------------- the workbook

/** F26019's headings in F26019's columns (row 4 of `F26019_Master_Refly_List_Day294.xlsx`). */
export const F26019_COLUMNS: ReadonlyArray<readonly [string, string]> = [
  ['A', 'Flt#'],
  ['B', 'Line #'],
  ['C', 'Reason'],
  ['E', 'Time Start'],
  ['F', 'SPM Start'],
  ['G', 'ZFID Start'],
  ['H', 'East Start'],
  ['I', 'North Start'],
  ['K', 'Time End'],
  ['L', 'SPM End'],
  ['M', 'ZFID End'],
  ['N', 'East End'],
  ['O', 'North End'],
  ['Q', 'Length Rejected'],
];

/**
 * F26019's office columns S to W (R is a spacer as D, J and P are): who made the patch, the two ticks (decision 544),
 * whether it was flown and in which flight.
 */
export const F26019_OFFICE_COLUMNS = [
  'Patch Made By',
  'Checked?',
  'Added to Acft?',
  'Flown?',
  'Flt#',
] as const;

/** Teslyn's columns after F26019's, from column X. */
export const TESLYN_REFLY_COLUMNS = [
  'Status',
  'Block',
  'Direction to fly',
  'Refly line',
  'Lines file',
  'Review reason',
  'Checked by',
  'Added to aircraft by',
] as const;

/** `Tylor, 2026-10-25 03:00 UTC` for a tick, empty when never ticked. */
function tickedBy(t: ReflyTickState | null | undefined): string | null {
  return t
    ? `${t.by_name}, ${t.at.slice(0, 16).replace('T', ' ')} UTC${t.ticked ? '' : ' (unticked)'}`
    : null;
}

export type SheetCell = string | number | null;

/** A sheet for the xlsx writer (`@teslyn/sync` `writeXlsx`). */
export interface WorkbookSheet {
  readonly name: string;
  /** Column widths in characters, column A first. */
  readonly widths: readonly number[];
  readonly rows: ReadonlyArray<readonly SheetCell[]>;
  /** Rows (0-based) printed bold. */
  readonly bold_rows: readonly number[];
  /** The heading row (0-based); the rows above it and it stay in view. */
  readonly header_row: number;
  /** Excel number format per column (`0.00`, `0`), null for the general format. */
  readonly formats: ReadonlyArray<string | null>;
}

export interface Workbook {
  readonly sheets: readonly WorkbookSheet[];
}

/** F26019's widths for A to Q (spacers 1.43), its S to W, then Teslyn's. */
const WIDTHS = [
  9, 9, 18, 1.43, 10, 10, 11.57, 11.57, 11.57, 1.43, 10, 10, 11.57, 11.57, 11.57, 1.43, 14, 1.43, 16, 10, 14,
  8, 6, 11, 18, 17, 10, 34, 40, 30, 30,
];
const FORMATS: ReadonlyArray<string | null> = [
  '0',
  '0',
  null,
  null,
  null,
  '0',
  '0',
  '0.00',
  '0.00',
  null,
  null,
  '0',
  '0',
  '0.00',
  '0.00',
  null,
  '0.0',
];

/**
 * The master refly list as a workbook (R24): sheet "Refly list" laid out as F26019 (a title and the date in rows 1
 * and 2, F26019's headings in row 4, one row per refly row) and sheet "Totals" (km still to refly per reason and per
 * block).
 */
export function reflyWorkbook(
  rows: readonly ReflyRow[],
  totals: ReflyTotals,
  meta: { readonly title: string; readonly asOf: string },
): Workbook {
  const teslyn = 18 + F26019_OFFICE_COLUMNS.length;
  const width = teslyn + TESLYN_REFLY_COLUMNS.length;
  const header: SheetCell[] = new Array<SheetCell>(width).fill(null);
  for (const [col, text] of F26019_COLUMNS) header[col.charCodeAt(0) - 65] = text;
  F26019_OFFICE_COLUMNS.forEach((t, i) => (header[18 + i] = t));
  TESLYN_REFLY_COLUMNS.forEach((t, i) => (header[teslyn + i] = t));
  const yes = (t: ReflyTickState | null | undefined): string | null => (t?.ticked ? 'Yes' : null);
  const body = rows.map((r): SheetCell[] => {
    const c: SheetCell[] = new Array<SheetCell>(width).fill(null);
    c[0] = r.flt_num;
    // decision 543 (2B): Line # as the screens write it, a number (F26019 wrote 10007.0)
    c[1] = r.line_number;
    c[2] = r.reason;
    c[4] = r.start.time;
    c[5] = r.start.spm;
    c[6] = r.start.zfid;
    c[7] = r.start.x;
    c[8] = r.start.y;
    c[10] = r.end.time;
    c[11] = r.end.spm;
    c[12] = r.end.zfid;
    c[13] = r.end.x;
    c[14] = r.end.y;
    c[16] = r.length_m;
    // F26019's S to W: Patch Made By, Checked?, Added to Acft? (decision 544: ticked by hand), Flown?, Flt#
    c[18] = r.patch_made_by ?? null;
    c[19] = yes(r.checked);
    c[20] = yes(r.on_aircraft);
    c[21] = r.status === 'flown' || r.status === 'accepted' ? 'Yes' : null;
    c[22] = r.reflown_flt_num;
    c[23] = REFLY_STATUS_WORDS[r.status];
    c[24] = r.block_name;
    c[25] = bearingWords(r.direction_deg);
    c[26] = r.refly_number;
    c[27] = r.lines_file;
    c[28] = r.reason_text || null;
    c[29] = tickedBy(r.checked);
    c[30] = tickedBy(r.on_aircraft);
    return c;
  });
  const sheet: WorkbookSheet = {
    name: 'Refly list',
    widths: WIDTHS,
    rows: [[`${meta.title}: master refly list`], [`As of ${meta.asOf}`], [], header, ...body],
    bold_rows: [0, 3],
    header_row: 3,
    formats: FORMATS,
  };
  const tRows: SheetCell[][] = [
    ['Km still to refly', null, null],
    [],
    ['Reason', 'Rows', 'Km'],
    ...totals.by_reason.map((r) => [r.reason, r.rows, r.km]),
    ['All open rows', totals.open_rows, Math.round(totals.open_km * 100) / 100],
    [],
    ['Block', 'Rows', 'Km'],
    ...totals.by_block.map((b) => [b.block_name, b.rows, b.km]),
  ];
  const blockHead = 3 + totals.by_reason.length + 2;
  return {
    sheets: [
      sheet,
      {
        name: 'Totals',
        widths: [24, 8, 10],
        rows: tRows,
        bold_rows: [0, 2, 3 + totals.by_reason.length, blockHead],
        header_row: 2,
        formats: [null, '0', '0.00'],
      },
    ],
  };
}

// ------------------------------------------------------------------------------------------------- plan and publish

/**
 * Decision 543 (5): every staff role publishes the refly list to the client portal (batch 4 had managers and office
 * staff only). Clients never do.
 */
export const REFLY_PUBLISH_ROLES = [
  'tenant_admin',
  'manager',
  'field_crew',
  'office_staff',
  'data_processor',
] as const;

/** Display only: `is_refly_publisher` (20261025001100_refly_publish_all_staff.sql) enforces the same. */
export function canPublishReflyList(roles: readonly string[]): boolean {
  return roles.some((r) => (REFLY_PUBLISH_ROLES as readonly string[]).includes(r));
}

/**
 * The rows as a client sees them once published (decision 565, Q501 B): with who made the patch and who ticked
 * Checked and Added to aircraft, but never the lines file names or the reasons as typed.
 */
export function reflyClientRows(rows: readonly ReflyRow[]): ReflyRow[] {
  return rows.map((r) => ({ ...r, reason_text: '', lines_file: null }));
}

/** Decision 544: every staff role ticks "Checked" and "Added to aircraft" (RLS of refly_tick, 20261025001200). */
export function canTickRefly(roles: readonly string[]): boolean {
  return canPublishReflyList(roles);
}

// ------------------------------------------------------------------------------------------------- patches by hand

/** What a patch made with the lines file tools does to the line in Line review (decision 544, 6B). */
export type ToolPatchPlan =
  /** The line has not been flown: the patch is for the nav system only, numbered as the line's next free version. */
  | { readonly kind: 'nav_only' }
  /** It cannot mark the line for a refly; the words say why. */
  | { readonly kind: 'refused'; readonly message: string }
  /**
   * As Line review would: the line's latest flown status part-accepted with this patch (`accepted_m`), the patch
   * planned as its next version (`change`, `patch`).
   */
  | {
      readonly kind: 'review';
      readonly status: LineStatus;
      readonly accepted_m: number;
      readonly change: PlanChange;
      readonly patch: PlannedLine;
    };

/** Line review's reason for a patch made with the lines file tools (at most 100 characters, Line review's limit). */
export const TOOL_PATCH_REASON = 'Patch made with the lines file tools';

/**
 * Decision 544 (6B): a patch made with the lines file tools marks that section of the line as needing a refly, as if
 * it were done in Line review: the latest flown status of the line is part-accepted with the patch planned as its next
 * version (`followUpPlan`, one patch per line, decision 155), so the refly list tracks it from Waiting to Accepted. A
 * line Line review rejected (a full reflight planned) is refused: change it there. A line that already has a patch
 * planned in Line review is refused too (decision 564, Q500 B). A line not flown yet gets the nav system's file only.
 */
export function toolPatchPlan(
  original: PlannedLine,
  segment: Segment,
  statuses: readonly LineStatus[],
  plan: readonly PlannedLine[],
  ids: { readonly now: string; readonly newId: () => Uuid },
): ToolPatchPlan {
  const flown = statuses
    .filter((s) => s.deleted_at === null && parseLineNumber(s.line_number).base === original.number)
    .sort((a, b) => b.line_number - a.line_number || b.created_at.localeCompare(a.created_at));
  const status = flown[0];
  if (!status) return { kind: 'nav_only' };
  if (status.acceptance === 'rejected')
    return {
      kind: 'refused',
      message: `Line ${status.line_number} is rejected in Line review, with a full reflight planned: part-accept it there to patch it instead.`,
    };
  const accepted_m = cm(
    Math.max(0, Math.min(status.flown_m, original.length_m - (segment.to_m - segment.from_m))),
  );
  const live = plan.filter((p) => p.deleted_at === null);
  const before = live.find(
    (p) =>
      parseLineNumber(p.number).base === original.number &&
      p.number > status.line_number &&
      plannedLineRole(p) === 'patch',
  );
  if (before)
    return {
      kind: 'refused',
      message: `A patch is already planned for line ${status.line_number} in Line review (${before.number}, ${((before.segment_from_m ?? 0) / 1000).toFixed(2)}–${((before.segment_to_m ?? before.length_m) / 1000).toFixed(2)} km): change it there.`,
    };
  let change: PlanChange;
  try {
    change = followUpPlan(status, { acceptance: 'partial', accepted_m, segment }, live, statuses, ids);
  } catch (e) {
    return { kind: 'refused', message: e instanceof Error ? e.message : String(e) };
  }
  const patch = change.save.find((p) => plannedLineRole(p) === 'patch');
  if (!patch)
    return {
      kind: 'refused',
      message: `Line ${status.line_number} cannot be patched: a later version of it has been flown. Patch that one.`,
    };
  return { kind: 'review', status, accepted_m, change, patch };
}

/** `F26048_Master_Refly_List_Day298.xlsx`, as MB's F26019 example is named. */
export function reflyWorkbookName(projectNumber: string, jday: number): string {
  return `${projectNumber}_Master_Refly_List_Day${String(jday).padStart(3, '0')}.xlsx`;
}

/** Chosen rows still to fly (not Flown, not Accepted), one group per block: one lines file each, in block order. */
export function reflyPlanGroups(rows: readonly ReflyRow[]): Array<{
  readonly block_id: string | null;
  readonly block_name: string;
  readonly rows: ReflyRow[];
}> {
  const groups = new Map<
    string,
    { block_id: string | null; block_name: string; digit: string; rows: ReflyRow[] }
  >();
  for (const r of rows) {
    if (r.status === 'flown' || r.status === 'accepted') continue;
    const key = r.block_id ?? '';
    const g = groups.get(key) ?? {
      block_id: r.block_id,
      block_name: r.block_name,
      digit: r.block_digit,
      rows: [],
    };
    g.rows.push(r);
    groups.set(key, g);
  }
  return [...groups.values()]
    .sort((a, b) => a.digit.localeCompare(b.digit))
    .map((g) => ({
      block_id: g.block_id,
      block_name: g.block_name,
      rows: [...g.rows].sort((a, b) => a.line_number - b.line_number),
    }));
}
